<script setup>
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';

// The channel cluster that feeds the inbox. Each entry resolves its logo through
// ChannelIcon so the scene stays in sync with the icons used elsewhere in the
// product. `position` arranges the chips as a crescent opening towards the
// inbox, and `delay` desynchronises the float so the cluster drifts organically
// instead of pulsing as one block.
const CHANNELS = [
  {
    id: 'messenger',
    inbox: { channel_type: 'Channel::FacebookPage' },
    unread: 8,
    position: 'left-[15%] top-[2%]',
    delay: '[animation-delay:0s]',
  },
  {
    id: 'whatsapp',
    inbox: { channel_type: 'Channel::Whatsapp' },
    unread: 12,
    position: 'left-[2%] top-[22%]',
    delay: '[animation-delay:-0.9s]',
  },
  {
    id: 'instagram',
    inbox: { channel_type: 'Channel::Instagram' },
    position: 'left-[0%] top-[43%]',
    delay: '[animation-delay:-1.8s]',
  },
  {
    id: 'tiktok',
    inbox: { channel_type: 'Channel::Tiktok' },
    unread: 3,
    position: 'left-[5%] top-[64%]',
    delay: '[animation-delay:-2.7s]',
  },
  {
    id: 'gmail',
    inbox: { channel_type: 'Channel::Email', provider: 'google' },
    unread: 44,
    position: 'left-[19%] top-[74%]',
    delay: '[animation-delay:-3.6s]',
  },
];

// Message bubbles travelling from the cluster into the inbox, one per lane.
const MESSAGES = [
  {
    id: 'top',
    animation: 'motion-safe:animate-onboarding-flow-top',
    delay: '',
  },
  {
    id: 'mid',
    animation: 'motion-safe:animate-onboarding-flow-mid',
    delay: '[animation-delay:1.1s]',
  },
  {
    id: 'bottom',
    animation: 'motion-safe:animate-onboarding-flow-bottom',
    delay: '[animation-delay:2.2s]',
  },
];

// Conversation rows landing in the inbox, tinted per originating channel.
const CONVERSATIONS = [
  { id: 'whatsapp', tint: 'bg-[#25D366]', delay: '' },
  { id: 'gmail', tint: 'bg-[#EA4335]', delay: '[animation-delay:1.6s]' },
  { id: 'messenger', tint: 'bg-[#A033FF]', delay: '[animation-delay:3.2s]' },
];
</script>

<template>
  <div class="absolute inset-0" aria-hidden="true">
    <div
      v-for="channel in CHANNELS"
      :key="channel.id"
      class="absolute grid rounded-xl border shadow-lg size-10 sm:size-11 place-items-center bg-n-solid-1 border-n-weak motion-safe:animate-onboarding-float"
      :class="[channel.position, channel.delay]"
    >
      <ChannelIcon :inbox="channel.inbox" use-brand-icon class="size-6" />
      <span
        v-if="channel.unread"
        class="absolute grid -top-1.5 -right-1.5 min-w-[1.0625rem] h-[1.0625rem] px-1 rounded-full bg-n-ruby-9 text-white text-[0.625rem] font-bold place-items-center"
      >
        {{ channel.unread }}
      </span>
    </div>

    <div
      v-for="message in MESSAGES"
      :key="message.id"
      class="hidden sm:block absolute w-5 h-3.5 rounded-[0.3125rem] opacity-0 bg-n-brand"
      :class="[message.animation, message.delay]"
    >
      <span
        class="absolute left-1.5 top-1 w-2.5 h-0.5 rounded-sm bg-white/80"
      />
      <span
        class="absolute left-1.5 top-2 w-1.5 h-0.5 rounded-sm bg-white/50"
      />
    </div>

    <div
      class="absolute right-[5%] top-1/2 -translate-y-1/2 w-[44%] max-w-[11.5rem] p-3.5 rounded-2xl border shadow-xl bg-n-solid-1 border-n-weak"
    >
      <div class="flex items-center gap-2 mb-3">
        <span
          class="rounded-full size-1.5 bg-n-teal-9 motion-safe:animate-loader-pulse"
        />
        <span
          class="text-[0.625rem] font-bold tracking-wider uppercase text-n-slate-11"
        >
          {{ $t('ONBOARDING.ALL_CONVERSATION.INBOX_LABEL') }}
        </span>
      </div>
      <div
        v-for="conversation in CONVERSATIONS"
        :key="conversation.id"
        class="flex items-center gap-2 mb-2.5 opacity-0 last:mb-0 motion-safe:animate-onboarding-conversation-in"
        :class="conversation.delay"
      >
        <span
          class="flex-none rounded-full size-5"
          :class="conversation.tint"
        />
        <span class="flex-1 space-y-1">
          <span class="block h-1 rounded-full bg-n-slate-5" />
          <span class="block w-3/5 h-1 rounded-full bg-n-slate-5" />
        </span>
      </div>
    </div>
  </div>
</template>
